import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTrackFunnelEvent } from '../hooks/useDiscovery';

const copy = {
  es: {
    eyebrow: 'Acceso a Los Recomendados',
    title: 'Inicia sesión',
    subtitle: 'Accede a tus recomendaciones guardadas y preferencias',
    loginTab: 'Entrar',
    registerTab: 'Registrarse',
    login: 'Entrar',
    register: 'Crear cuenta',
    email: 'Correo electrónico',
    password: 'Contraseña',
    forgotPassword: '¿Olvidaste tu contraseña?',
    noAccount: '¿No tienes cuenta? ',
    hasAccount: '¿Ya tienes cuenta? ',
    createAccount: 'Crear cuenta',
    signIn: 'Iniciar sesión',
    or: 'o',
    continueWith: 'Continuar con',
    terms: 'Al continuar, aceptas nuestros ',
    termsLink: 'Términos de servicio',
    and: ' y ',
    privacyLink: 'Política de privacidad',
  },
  en: {
    eyebrow: 'Access Los Recomendados',
    title: 'Sign in',
    subtitle: 'Access your saved recommendations and preferences',
    loginTab: 'Sign in',
    registerTab: 'Sign up',
    login: 'Sign in',
    register: 'Create account',
    email: 'Email',
    password: 'Password',
    forgotPassword: 'Forgot password?',
    noAccount: "Don't have an account? ",
    hasAccount: 'Already have an account? ',
    createAccount: 'Create account',
    signIn: 'Sign in',
    or: 'or',
    continueWith: 'Continue with',
    terms: 'By continuing, you agree to our ',
    termsLink: 'Terms of Service',
    and: ' and ',
    privacyLink: 'Privacy Policy',
  },
};

export function AuthScreen() {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register } = useAuth();
  const { track } = useTrackFunnelEvent();

  const t = copy.es;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Correo electrónico y Contraseña son requeridos');
      return;
    }

    setLoading(true);

    try {
      if (mode === 'login') {
        await login(email, password);
        track({ type: 'login', metadata: { method: 'email', returning_user: true } });
      } else {
        await register(email, password);
        track({ type: 'signup', metadata: { method: 'email', role: 'consumer' } });
      }
      const next = searchParams.get('next');
      navigate(next && next.startsWith('/') ? next : '/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error de autenticación');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <p className="text-sm text-gray-500 mb-2">{t.eyebrow}</p>
          <h1 className="text-3xl font-bold text-gray-900">{t.title}</h1>
          <p className="mt-2 text-gray-600">{t.subtitle}</p>
        </div>

        <div className="bg-white py-8 px-6 shadow-sm rounded-xl border border-gray-100">
          <div className="flex gap-4 mb-6 border-b border-gray-100">
            <button
              type="button"
              onClick={() => setMode('login')}
              className={`flex-1 py-3 text-center font-medium text-sm transition-colors ${
                mode === 'login'
                  ? 'text-gray-900 border-b-2 border-gray-900'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.loginTab}
            </button>
            <button
              type="button"
              onClick={() => setMode('register')}
              className={`flex-1 py-3 text-center font-medium text-sm transition-colors ${
                mode === 'register'
                  ? 'text-gray-900 border-b-2 border-gray-900'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.registerTab}
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="label">
                {t.email}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="tu@email.com"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="password" className="label">
                  {t.password}
                </label>
                <a href="#" className="text-sm text-primary hover:underline">
                  {t.forgotPassword}
                </a>
              </div>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                placeholder="••••••••"
              />
            </div>

            {mode === 'register' && (
              <p className="text-xs text-gray-500 -mt-2">
                Registrarse es gratis. Guarda y organiza tus recomendaciones favoritas.
              </p>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-3"
            >
              {loading ? 'Cargando...' : mode === 'login' ? t.signIn : t.createAccount}
            </button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">{t.or}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-6">
              <button
                type="button"
                className="btn-secondary flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-1.94-1.94c-.98.65-2.05 1-3.21 1-2.39 0-4.42-1.58-4.82-3.72l-2.9 2.81C9.75 18.43 10.66 19 12 19c4.83 0 8.84-3.87 8.84-8.84 0-.45-.05-.89-.11-1.33H12V12h8.42c-.85 2.58-3.75 4.74-7.42 5.45v2.71z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-1.94-1.94c-.98.65-2.05 1-3.21 1-2.39 0-4.42-1.58-4.82-3.72l-2.9 2.81C9.75 18.43 10.66 19 12 19c4.83 0 8.84-3.87 8.84-8.84 0-.45-.05-.89-.11-1.33H12V12h8.42c-.85 2.58-3.75 4.74-7.42 5.45v2.71z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-1.94-1.94c-.98.65-2.05 1-3.21 1-2.39 0-4.42-1.58-4.82-3.72l-2.9 2.81C9.75 18.43 10.66 19 12 19c4.83 0 8.84-3.87 8.84-8.84 0-.45-.05-.89-.11-1.33H12V12h8.42c-.85 2.58-3.75 4.74-7.42 5.45v2.71z"/>
                </svg>
                <span>Google</span>
              </button>
              <button className="btn-secondary flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 4.301 9.578 9.601 9.891.699.111.948-.227.948-.503 0-.247-.035-.898-.048-1.987-2.316.398-3.215-1.124-3.387-2.38-.11-.26-.27-.725.048-.89.098-.063.384-.21.658-.305C6.448 11.81 4.737 10.66 4.737 8.427 0 5.093 3.855 2.167 8.298 2.167c2.663 0 4.843 1.503 4.843 3.367 0 1.906-.943 2.992-1.955 2.992-1.263 0-2.01-.977-1.765-1.757.132-.41.436-.754.895-1.083.212-.15.484-.33.84-.456.257-.07.52-.122.82-.122.486 0 .783.086.995.134.35-.048.64-.16.916-.326C6.315 7.94 6 6.96 6 5.515c0-1.775 2.242-3.5 5.004-3.5 2.53 0 3.976 1.834 3.976 3.933 0 1.087-.487 2.22-.95 2.745-.038.042-.05.143-.05.312 0 .5.067.893.185 1.1.147.28.404.67.718.815C19.99 15.35 20 12.97 20 11.077c0-3.486-3.55-6.587-10.7-6.587-2.353 0-4.363 1.01-5.857 2.572-.314.33-.62.69-.907 1.029C5.38 6.648 5.13 7.05 5.13 7.575c0 .747.366 1.283.823 1.606C3.56 9.12 4.08 12 4.08 12c.99 2.08 2.79 3.5 6.06 3.5 3.93 0 6.06-3.26 6.06-7.2 0-2.64-1.55-4.22-3.75-4.22-1.54 0-2.75 1.24-2.75 2.83 0 1.33.55 2.02 1.16 1.706.17.195.36.35.55.46 1.57-.79 2.5-2.5 2.5-4.41 0-3.77-2.6-4.8-5.77-.98-.36-.78-.8-1.07-1.12A36.68 36.68 0 0112 1.95c6.66 0 12 5.37 12 12 0 6.67-5.37 12-12 12z" fill="#24292E"/>
                </svg>
                <span>GitHub</span>
              </button>
            </div>

            <p className="mt-6 text-center text-sm text-gray-600">
              {t.noAccount}
              <button
                type="button"
                onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
                className="font-medium text-primary hover:underline"
              >
                {mode === 'login' ? t.createAccount : t.signIn}
              </button>
            </p>

            <p className="mt-6 text-center text-xs text-gray-500">
              {t.terms}
              <a href="#" className="text-primary hover:underline">{t.termsLink}</a>
              {t.and}
              <a href="#" className="text-primary hover:underline">{t.privacyLink}</a>
            </p>
        </div>
      </div>
      </div>
    </div>
  );
}